import { getClient, gqlFromString } from "lib/experience-api";
import { GET_QUIZ_QUESTION_RESPONSE_GRADING } from "lib/experience-api/queries";
import { GRADING_STATUS } from "./constants";
import { buildScopeTranslate } from "lib/I18n";

const tErrors = buildScopeTranslate("views.content.agentforce.assessments.errors");

export const FIRST_POLL_MS = 7000;
export const MAX_POLLS = 6;
export const TOTAL_MS = 30000;

export function buildPollSchedule(firstMs, maxPolls, totalMs) {
  const remaining = totalMs - firstMs;
  const slots = maxPolls - 1;
  const interval = Math.floor(remaining / slots);
  const lastInterval = remaining - interval * (slots - 1);
  return [firstMs, ...Array(slots - 1).fill(interval), lastInterval];
}

export const DEFAULT_POLL_SCHEDULE = buildPollSchedule(FIRST_POLL_MS, MAX_POLLS, TOTAL_MS);

export function startGradingPoller({
  quizAttemptId,
  quizQuestionId,
  topicApiName,
  onComplete,
  schedule = DEFAULT_POLL_SCHEDULE,
  timeoutMessage = tErrors("unexpected_error"),
}) {
  const gradingQuery = gqlFromString(GET_QUIZ_QUESTION_RESPONSE_GRADING);
  let cancelled = false;
  let timeoutId = null;
  let resolveOuter;

  const cancel = () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
    if (resolveOuter) resolveOuter();
  };

  const promise = new Promise((resolve, reject) => {
    resolveOuter = resolve;

    const runLoop = async () => {
      for (let index = 0; index < schedule.length; index++) {
        if (cancelled) return resolve();

        let resolveWait;
        const waitPromise = new Promise((r) => { resolveWait = r; });
        timeoutId = setTimeout(resolveWait, schedule[index]);
        await waitPromise;
        if (cancelled) return resolve();

        let result;
        try {
          result = await getClient().query({
            query: gradingQuery,
            variables: { quizAttemptId, quizQuestionId, topicApiName },
            fetchPolicy: "network-only",
          });
        } catch (error) {
          return reject(error);
        }
        if (cancelled) return resolve();

        // status=COMPLETE without attempt data means the job finished but the attempt
        // record is not yet available in the response. Continue polling rather than
        // calling onComplete with incomplete data.
        const gradingResult = result?.data?.getQuizQuestionResponseGrading;
        if (gradingResult?.status === GRADING_STATUS.COMPLETE && gradingResult.attempt) {
          onComplete(gradingResult);
          return resolve();
        }
      }

      // All schedule slots exhausted without COMPLETE
      if (!cancelled) reject(new Error(timeoutMessage));
    };

    runLoop().catch((err) => {
      if (!cancelled) reject(err);
    });
  });

  // Suppress unhandled-rejection warnings when callers don't consume the promise
  // (e.g., tests that only check intermediate side-effects). The rejection state
  // is preserved — consumers that do `await promise` still see the rejection.
  promise.catch(() => {});

  return { promise, cancel };
}
